import {
  Box,
  Center,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Square,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { ButtonLink } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import type { NavigationPage } from "@src/components/widgets/Layout/AppNav/AppNav";
import type { TPages } from "@src/config/routes";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { getUrlSegments } from "@src/utils/getUrlSegments";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import Link from "next/link";
import { useRouter } from "next/router";
import { type FunctionComponent, useEffect, useState } from "react";

export type NavItemProps = {
  targetId: TPages;
} & NavigationPage;

export const NavItem: FunctionComponent<NavItemProps> = ({
  url,
  subPages,
  activeOn = [],
  label,
  icon,
}) => {
  const { UIStore } = useStore();
  const [active, setActive] = useState(false);
  const router = useRouter();
  const { isDesktop } = useScreenType();

  useEffect(() => {
    setActive(
      matchFirstUrlSegment(router.pathname, [
        url ?? subPages[0].url,
        ...activeOn,
      ]),
    );
  }, [router.pathname, url, activeOn]);

  return (
    <WrapComponent
      if={!!url}
      with={(children) => {
        // just to avoid ts error as typescript is not smart enough to understand that we are checking for url above
        if (!url) return children;
        return (
          <Tooltip
            px="3"
            py="2"
            color="grey.800"
            bg="riptide.300"
            gutter={9}
            hasArrow={false}
            label={
              <Text fontSize="sm" fontWeight="medium">
                {label}
              </Text>
            }
            openDelay={200}
            placement={isDesktop ? "right" : "top"}
            rounded="md"
          >
            <Link
              href={url}
              style={{ height: isDesktop ? "auto" : "100%" }}
              onClick={async (e) => {
                e.preventDefault();
                await UIStore.onBackButtonClick?.();
                router.push(url);
              }}
            >
              {children}
            </Link>
          </Tooltip>
        );
      }}
      elseWith={(children) => (
        <Popover
          placement={isDesktop ? "right-start" : "top-start"}
          trigger="hover"
        >
          <PopoverTrigger>
            <Box>
              <WrapComponent
                if={isDesktop}
                with={(children) => (
                  <Link href={subPages![0].url}>{children}</Link>
                )}
              >
                <Center pos="relative">
                  {children}
                  <Icon
                    pos="absolute"
                    right={isDesktop ? 0 : "initial"}
                    top={isDesktop ? "initial" : 0}
                    name={isDesktop ? "dots-vertical" : "dots-horizontal"}
                    color="grey.800"
                  />
                </Center>
              </WrapComponent>
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent minW="200px" maxW="fit-content" bg="riptide.300">
              <PopoverHeader
                px="4"
                color="riptide.800"
                fontSize="2xs"
                fontWeight="semibold"
                textTransform="uppercase"
                borderColor="riptide.400"
              >
                {label}
              </PopoverHeader>
              <PopoverBody p="1">
                <Stack spacing="0">
                  {subPages?.map(({ label, url }) => (
                    <ButtonLink
                      w="full"
                      justifyContent="start"
                      size="sm"
                      href={url}
                      key={url}
                      variant="ghost"
                      color="grey.800"
                      _hover={{ bg: "riptide.200" }}
                      colorScheme="grey"
                    >
                      {label}
                    </ButtonLink>
                  ))}
                </Stack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    >
      <Center w="20" h={{ base: "20", lg: "auto" }} role="group">
        <Square
          bg={active ? "riptide.200" : "transparent"}
          _groupHover={{ bg: "riptide.200" }}
          rounded="md"
          size="48px"
        >
          <Icon
            name={icon}
            w="6"
            h="6"
            color={active ? "violet.700" : "grey.800"}
            _groupHover={{ color: "blue.600" }}
          />
        </Square>
      </Center>
    </WrapComponent>
  );
};

const matchFirstUrlSegment = (current: string, toMatch: string[]): boolean => {
  for (const toMatchPathname of toMatch) {
    const firstSegment = getUrlSegments(current)[0];
    const toMatchFirstSegment = getUrlSegments(toMatchPathname)[0];

    if (firstSegment === null || toMatchFirstSegment === null) return false;

    if (firstSegment === toMatchFirstSegment) {
      return true;
    }
  }

  return false;
};
